import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";
import { Link, Navigate } from "react-router-dom";
import type {
  CapabilityCatalogItemV1,
  CapabilityCatalogListV1,
  CapabilityCatalogSourceSummaryV1,
} from "@dosia/shared";
import { apiCall } from "../lib/api-client.js";
import { platformCapabilitiesPath, platformCapabilityPath } from "../lib/api-paths.js";
import { ApiError } from "../lib/api-types.js";
import { authAtom } from "../lib/auth-store.js";
import { formatDateTime } from "../lib/format.js";
import {
  computeCatalogStats,
  collectSourceOptions,
  DEFAULT_CAPABILITY_FILTERS,
  filterCapabilityItems,
  isFilterActive,
  normalizeCatalogError,
  parseCatalogDetailResponse,
  parseCatalogListResponse,
  type CapabilityCatalogFilters,
  type CatalogAssetKindFilter,
  type CatalogDeliveryFilter,
  type CatalogHealthFilter,
  type CatalogLoadError,
  type CatalogRegistrationFilter,
} from "../lib/platform-capabilities.js";
import { ADMIN_LOGIN, ADMIN_SKILL_HUB, tenantPath } from "../lib/paths.js";
import { Drawer } from "../components/Drawer.js";
import { Empty } from "../components/Empty.js";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { PrimaryButton, SecondaryButton, Select, TextInput } from "../components/FormField.js";
import { PageHeader } from "../components/PageHeader.js";
import { InlineLoading } from "../components/Spinner.js";
import { Table, type ColumnDef } from "../components/Table.js";

/**
 * 平台能力总库（P0C）—— super/support 只读目录。
 * 数据经 shared strict parser；observed 不推断 Release/交付；不造 demo 行。
 * 供给包生命周期仍走 /admin/skill-hub。
 *
 * List/detail use dedicated fail-closed state (loading | ready | error).
 * Never keep stale rows alongside errors; null/204 → contract error.
 */

type ListViewState =
  | { status: "loading" }
  | { status: "ready"; data: CapabilityCatalogListV1 }
  | { status: "error"; error: CatalogLoadError };

function readinessClass(status: string): string {
  if (status === "ready") {
    return "bg-dosia-success/15 text-dosia-success border-dosia-success/40";
  }
  if (status === "blocked") {
    return "bg-dosia-warning/15 text-dosia-warning border-dosia-warning/40";
  }
  return "bg-dosia-bg-lighter text-dosia-text-secondary border-dosia-border";
}

function Pill({ label, className }: { label: string; className: string }): JSX.Element {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${className}`}
    >
      {label}
    </span>
  );
}

function DetailField({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="space-y-0.5">
      <div className="text-xs text-dosia-text-secondary">{label}</div>
      <div className="text-sm text-dosia-text break-words">{value}</div>
    </div>
  );
}

function availabilityLabel(
  item: CapabilityCatalogItemV1,
  t: (key: string) => string,
): string {
  if (item.availability.use) return t("admin.capabilities.availability.use");
  if (item.availability.discover) return t("admin.capabilities.availability.discover");
  return t("admin.capabilities.availability.unavailable");
}

export function PlatformCapabilityCatalogGate(): JSX.Element {
  const auth = useAtomValue(authAtom);
  if (auth?.scope === "org-admin") {
    if (auth.orgCode) {
      return <Navigate to={tenantPath(auth.orgCode)} replace />;
    }
    // Abnormal auth: org-admin without orgCode must not hit platform catalog.
    return <Navigate to={ADMIN_LOGIN} replace />;
  }
  return <PlatformCapabilityCatalog />;
}

export function PlatformCapabilityCatalog(): JSX.Element {
  const { t } = useTranslation();
  const token = useAtomValue(authAtom)?.token;

  const [listState, setListState] = useState<ListViewState>({ status: "loading" });
  const listSeqRef = useRef(0);
  const listAbortRef = useRef<AbortController | null>(null);

  const [filters, setFilters] = useState<CapabilityCatalogFilters>(DEFAULT_CAPABILITY_FILTERS);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailItem, setDetailItem] = useState<CapabilityCatalogItemV1 | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<CatalogLoadError | null>(null);
  const detailSeqRef = useRef(0);
  const detailTriggerRef = useRef<HTMLElement | null>(null);
  const drawerTitleId = "platform-capability-detail-title";

  const loadList = useCallback(async (): Promise<void> => {
    if (!token) return;
    listAbortRef.current?.abort();
    const controller = new AbortController();
    listAbortRef.current = controller;
    const seq = ++listSeqRef.current;
    // Fail-closed: clear previous ready rows immediately on (re)fetch.
    setListState({ status: "loading" });
    try {
      const raw = await apiCall<unknown>("GET", platformCapabilitiesPath(), {
        token,
        signal: controller.signal,
      });
      if (controller.signal.aborted || seq !== listSeqRef.current) return;
      try {
        const data = parseCatalogListResponse(raw);
        setListState({ status: "ready", data });
      } catch {
        setListState({
          status: "error",
          error: {
            kind: "contract",
            message: t("admin.capabilities.error.contract"),
          },
        });
      }
    } catch (err) {
      if (controller.signal.aborted || seq !== listSeqRef.current) return;
      if (err instanceof ApiError || err instanceof TypeError || err instanceof Error) {
        setListState({
          status: "error",
          error: normalizeCatalogError(err, t("admin.capabilities.error.load")),
        });
        return;
      }
      setListState({
        status: "error",
        error: normalizeCatalogError(err, t("admin.capabilities.error.load")),
      });
    }
  }, [token, t]);

  useEffect(() => {
    void loadList();
    return () => {
      listAbortRef.current?.abort();
      listSeqRef.current += 1;
    };
  }, [loadList]);

  const loadDetail = useCallback(
    async (catalogItemId: string, triggerEl?: HTMLElement | null): Promise<void> => {
      if (!token) return;
      if (triggerEl) detailTriggerRef.current = triggerEl;
      const seq = ++detailSeqRef.current;
      setSelectedId(catalogItemId);
      setDetailItem(null);
      setDetailError(null);
      setDetailLoading(true);
      try {
        const raw = await apiCall<unknown>("GET", platformCapabilityPath(catalogItemId), {
          token,
        });
        if (seq !== detailSeqRef.current) return;
        try {
          const item = parseCatalogDetailResponse(raw, catalogItemId);
          setDetailItem(item);
          setDetailError(null);
        } catch {
          setDetailItem(null);
          setDetailError({
            kind: "contract",
            message: t("admin.capabilities.detail.contractError"),
          });
        }
      } catch (err) {
        if (seq !== detailSeqRef.current) return;
        setDetailItem(null);
        // Transport / ApiError only — never contract for fetch TypeError.
        setDetailError(normalizeCatalogError(err, t("admin.capabilities.detail.error")));
      } finally {
        if (seq === detailSeqRef.current) {
          setDetailLoading(false);
        }
      }
    },
    [token, t],
  );

  const closeDetail = useCallback((): void => {
    // Invalidate in-flight detail responses so late arrivals cannot write UI.
    detailSeqRef.current += 1;
    setSelectedId(null);
    setDetailItem(null);
    setDetailError(null);
    setDetailLoading(false);
  }, []);

  const items = listState.status === "ready" ? listState.data.items : [];
  const sourceSummaries: readonly CapabilityCatalogSourceSummaryV1[] =
    listState.status === "ready" ? listState.data.sourceSummaries : [];

  const stats = useMemo(
    () => computeCatalogStats(items, sourceSummaries),
    [items, sourceSummaries],
  );
  const sourceOptions = useMemo(() => collectSourceOptions(items), [items]);
  const filtered = useMemo(
    () => filterCapabilityItems(items, filters),
    [items, filters],
  );
  const filtersActive = isFilterActive(filters);

  const assetKindLabel = (kind: string): string => {
    switch (kind) {
      case "skill":
        return t("admin.capabilities.assetKind.skill");
      case "agent":
        return t("admin.capabilities.assetKind.agent");
      case "workflow":
        return t("admin.capabilities.assetKind.workflow");
      case "job_template":
        return t("admin.capabilities.assetKind.job_template");
      default:
        return kind;
    }
  };

  const healthLabel = (status: string): string => {
    switch (status) {
      case "ready":
        return t("admin.capabilities.health.ready");
      case "blocked":
        return t("admin.capabilities.health.blocked");
      case "unknown":
        return t("admin.capabilities.health.unknown");
      default:
        return status;
    }
  };

  const deliveryCell = (item: CapabilityCatalogItemV1): JSX.Element => {
    if (item.catalogState === "observed") {
      return (
        <div className="space-y-0.5 text-xs text-dosia-text-secondary">
          <div>{t("admin.capabilities.observed.notRegistered")}</div>
          <div>{t("admin.capabilities.observed.deliveryUndeclared")}</div>
        </div>
      );
    }
    const delivery =
      item.deliveryMode === "local_editable"
        ? t("admin.capabilities.delivery.local_editable")
        : t("admin.capabilities.delivery.remote_protected");
    const deployment = t(`admin.capabilities.deployment.${item.deployment}`);
    return (
      <div className="space-y-0.5 text-xs">
        <div className="text-dosia-text">{delivery}</div>
        <div className="text-dosia-text-secondary">{deployment}</div>
      </div>
    );
  };

  const versionCell = (item: CapabilityCatalogItemV1): JSX.Element => {
    if (item.catalogState === "observed") {
      return (
        <div className="space-y-0.5 text-xs text-dosia-text-secondary">
          <div>{t("admin.capabilities.catalogState.observed")}</div>
          <div>{t("admin.capabilities.observed.noRelease")}</div>
        </div>
      );
    }
    return (
      <div className="space-y-0.5 text-xs">
        <div className="font-mono text-dosia-text">{item.version}</div>
        <div className="text-dosia-text-secondary">
          {t(`admin.capabilities.releaseStatus.${item.status}`)}
        </div>
      </div>
    );
  };

  const columns: ColumnDef<CapabilityCatalogItemV1>[] = [
    {
      header: t("admin.capabilities.table.capability"),
      render: (row) => (
        <div className="min-w-[12rem]">
          <button
            type="button"
            className="font-medium text-dosia-text text-left rounded-sm hover:text-dosia-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dosia-accent"
            onClick={(e) => {
              e.stopPropagation();
              void loadDetail(row.catalogItemId, e.currentTarget);
            }}
          >
            {row.name}
          </button>
          <div className="mt-0.5 text-xs text-dosia-text-secondary line-clamp-2">
            {row.description}
          </div>
        </div>
      ),
    },
    {
      header: t("admin.capabilities.table.type"),
      width: "w-28",
      render: (row) => (
        <span className="text-xs text-dosia-text">{assetKindLabel(row.assetKind)}</span>
      ),
    },
    {
      header: t("admin.capabilities.table.source"),
      width: "w-36",
      render: (row) => (
        <div className="space-y-0.5 text-xs text-dosia-text-secondary">
          <div>{row.source.sourceKind}</div>
          <div className="text-dosia-text-dim">{row.sourceCoverage.sources.join(", ")}</div>
        </div>
      ),
    },
    {
      header: t("admin.capabilities.table.delivery"),
      width: "w-36",
      render: deliveryCell,
    },
    {
      header: t("admin.capabilities.table.version"),
      width: "w-36",
      render: versionCell,
    },
    {
      header: t("admin.capabilities.table.health"),
      width: "w-28",
      render: (row) => (
        <div className="space-y-1">
          <Pill
            label={healthLabel(row.sourceCoverage.readiness.status)}
            className={readinessClass(row.sourceCoverage.readiness.status)}
          />
          <div className="text-[11px] text-dosia-text-dim">{availabilityLabel(row, t)}</div>
        </div>
      ),
    },
  ];

  const skillHubLink = (): JSX.Element => (
    <Link
      to={ADMIN_SKILL_HUB}
      className="inline-flex items-center rounded-md bg-dosia-accent px-3 py-1.5 text-sm font-medium text-dosia-bg-light hover:opacity-90"
      data-testid="capabilities-open-skill-hub"
    >
      {t("admin.capabilities.action.skillHub")}
    </Link>
  );

  const listError = listState.status === "error" ? listState.error : null;
  const listReady = listState.status === "ready";
  const listLoading = listState.status === "loading";

  return (
    <div className="max-w-6xl space-y-4" data-testid="platform-capability-catalog">
      <PageHeader
        title={t("admin.capabilities.title")}
        subtitle={t("admin.capabilities.subtitle")}
        actions={skillHubLink()}
      />

      <section
        className="rounded-md border border-dosia-border bg-dosia-bg-light px-3 py-2 text-xs text-dosia-text-secondary space-y-1"
        data-testid="capabilities-boundary"
      >
        <p>{t("admin.capabilities.boundary.plugin")}</p>
        <p>{t("admin.capabilities.boundary.workflow")}</p>
        <p>{t("admin.capabilities.boundary.observed")}</p>
      </section>

      {listError ? (
        <ErrorBanner>
          <div className="flex flex-wrap items-center gap-2">
            <span>{listError.message}</span>
            <SecondaryButton type="button" onClick={() => void loadList()}>
              {t("admin.capabilities.error.retry")}
            </SecondaryButton>
            {listError.kind !== "contract" ? (
              <Link
                to={ADMIN_SKILL_HUB}
                className="text-sm text-dosia-accent underline-offset-2 hover:underline"
              >
                {t("admin.capabilities.error.goSkillHub")}
              </Link>
            ) : null}
          </div>
        </ErrorBanner>
      ) : null}

      {listLoading ? (
        <InlineLoading label={t("admin.capabilities.loading")} />
      ) : null}

      {listReady ? (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="capabilities-stats">
            {[
              [t("admin.capabilities.stats.total"), stats.total, ""],
              [t("admin.capabilities.stats.skills"), stats.skillCount, ""],
              [t("admin.capabilities.stats.agents"), stats.agentCount, ""],
              [
                t("admin.capabilities.stats.sourceHealth"),
                t("admin.capabilities.healthSummary.compact", {
                  ready: stats.sourceHealth.ready,
                  blocked: stats.sourceHealth.blocked,
                  unknown: stats.sourceHealth.unknown,
                }),
                "",
              ],
            ].map(([label, value, cls]) => (
              <div
                key={String(label)}
                className="rounded border border-dosia-border bg-dosia-bg-light px-3 py-2"
              >
                <div className="text-xs text-dosia-text-secondary">{label}</div>
                <div className={`mt-1 text-xl font-semibold ${cls || "text-dosia-text"}`}>
                  {value}
                </div>
              </div>
            ))}
          </div>

          <section className="space-y-2" data-testid="capabilities-source-summaries">
            <h2 className="text-sm font-medium text-dosia-text">
              {t("admin.capabilities.sourceSummary.title")}
            </h2>
            <div className="grid gap-2 md:grid-cols-2">
              {sourceSummaries.map((s) => (
                <div
                  key={s.sourceKind}
                  className="rounded border border-dosia-border bg-dosia-bg-light px-3 py-2 text-xs space-y-1"
                  data-testid={`source-summary-${s.sourceKind}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-dosia-text">{s.sourceKind}</span>
                    <Pill
                      label={healthLabel(s.readiness.status)}
                      className={readinessClass(s.readiness.status)}
                    />
                  </div>
                  <div className="text-dosia-text-secondary">
                    {t("admin.capabilities.sourceSummary.itemCount")}: {s.itemCount}
                  </div>
                  <div className="text-dosia-text-secondary">
                    {t("admin.capabilities.sourceSummary.provenanceOnly")}: {s.provenanceOnlyCount}
                  </div>
                  <div className="text-dosia-text-dim">
                    {t("admin.capabilities.sourceSummary.updatedAt")}: {formatDateTime(s.updatedAt)}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <div
            className="flex flex-wrap items-end gap-2"
            data-testid="capabilities-filters"
          >
            <label className="flex min-w-[12rem] flex-1 flex-col gap-1 text-xs text-dosia-text-secondary">
              {t("admin.capabilities.filter.search")}
              <TextInput
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                placeholder={t("admin.capabilities.filter.searchPlaceholder")}
                aria-label={t("admin.capabilities.filter.search")}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-dosia-text-secondary">
              {t("admin.capabilities.filter.assetKind")}
              <Select
                value={filters.assetKind}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    assetKind: e.target.value as CatalogAssetKindFilter,
                  }))
                }
                aria-label={t("admin.capabilities.filter.assetKind")}
              >
                <option value="all">{t("admin.capabilities.filter.all")}</option>
                <option value="skill">{t("admin.capabilities.assetKind.skill")}</option>
                <option value="agent">{t("admin.capabilities.assetKind.agent")}</option>
                <option value="workflow">{t("admin.capabilities.assetKind.workflow")}</option>
                <option value="job_template">
                  {t("admin.capabilities.assetKind.job_template")}
                </option>
              </Select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-dosia-text-secondary">
              {t("admin.capabilities.filter.source")}
              <Select
                value={filters.source}
                onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))}
                aria-label={t("admin.capabilities.filter.source")}
              >
                <option value="all">{t("admin.capabilities.filter.all")}</option>
                {sourceOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-dosia-text-secondary">
              {t("admin.capabilities.filter.delivery")}
              <Select
                value={filters.delivery}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    delivery: e.target.value as CatalogDeliveryFilter,
                  }))
                }
                aria-label={t("admin.capabilities.filter.delivery")}
              >
                <option value="all">{t("admin.capabilities.filter.all")}</option>
                <option value="unregistered">
                  {t("admin.capabilities.delivery.unregistered")}
                </option>
                <option value="local_editable">
                  {t("admin.capabilities.delivery.local_editable")}
                </option>
                <option value="remote_protected">
                  {t("admin.capabilities.delivery.remote_protected")}
                </option>
              </Select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-dosia-text-secondary">
              {t("admin.capabilities.filter.catalogState")}
              <Select
                value={filters.catalogState}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    catalogState: e.target.value as CatalogRegistrationFilter,
                  }))
                }
                aria-label={t("admin.capabilities.filter.catalogState")}
              >
                <option value="all">{t("admin.capabilities.filter.all")}</option>
                <option value="observed">
                  {t("admin.capabilities.catalogState.observed")}
                </option>
                <option value="registered">
                  {t("admin.capabilities.catalogState.registered")}
                </option>
              </Select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-dosia-text-secondary">
              {t("admin.capabilities.filter.health")}
              <Select
                value={filters.health}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    health: e.target.value as CatalogHealthFilter,
                  }))
                }
                aria-label={t("admin.capabilities.filter.health")}
              >
                <option value="all">{t("admin.capabilities.filter.all")}</option>
                <option value="ready">{t("admin.capabilities.health.ready")}</option>
                <option value="blocked">{t("admin.capabilities.health.blocked")}</option>
                <option value="unknown">{t("admin.capabilities.health.unknown")}</option>
              </Select>
            </label>
          </div>

          {items.length === 0 ? (
            <Empty
              title={t("admin.capabilities.empty.catalog")}
              description={t("admin.capabilities.empty.catalogDesc")}
              action={skillHubLink()}
            />
          ) : (
            <Table
              columns={columns}
              rows={filtered}
              rowKey={(row) => row.catalogItemId}
              onRowClick={(row) => void loadDetail(row.catalogItemId)}
              empty={
                filtersActive ? (
                  <Empty
                    title={t("admin.capabilities.empty.filter")}
                    description={t("admin.capabilities.empty.filterDesc")}
                  />
                ) : (
                  <Empty title={t("admin.capabilities.empty.catalog")} />
                )
              }
            />
          )}
        </>
      ) : null}

      <Drawer
        open={selectedId != null}
        onClose={closeDetail}
        title={detailItem?.name ?? t("admin.capabilities.detail.title")}
        titleId={drawerTitleId}
        closeLabel={t("admin.capabilities.detail.close")}
        returnFocusRef={detailTriggerRef}
        width={520}
      >
        {detailLoading ? (
          <InlineLoading label={t("admin.capabilities.detail.loading")} />
        ) : null}
        {detailError ? (
          <div className="space-y-2">
            <ErrorBanner>{detailError.message}</ErrorBanner>
            {selectedId ? (
              <PrimaryButton type="button" onClick={() => void loadDetail(selectedId)}>
                {t("admin.capabilities.error.retry")}
              </PrimaryButton>
            ) : null}
          </div>
        ) : null}
        {detailItem && !detailLoading ? (
          <div className="space-y-3" data-testid="capabilities-detail">
            {detailItem.catalogState === "observed" ? (
              <div className="rounded border border-dosia-warning/40 bg-dosia-warning/10 px-3 py-2 text-xs text-dosia-warning">
                {t("admin.capabilities.detail.observedNotice")}
              </div>
            ) : null}
            <DetailField
              label={t("admin.capabilities.detail.description")}
              value={detailItem.description}
            />
            <DetailField
              label={t("admin.capabilities.detail.assetKind")}
              value={assetKindLabel(detailItem.assetKind)}
            />
            <DetailField
              label={t("admin.capabilities.detail.source")}
              value={`${detailItem.source.sourceKind} · ${detailItem.source.sourceId}`}
            />
            <DetailField
              label={t("admin.capabilities.detail.owner")}
              value={
                detailItem.owner.kind === "platform"
                  ? t("admin.capabilities.owner.platform")
                  : `${t(`admin.capabilities.owner.${detailItem.owner.kind}`)}${
                      detailItem.owner.id ? ` · ${detailItem.owner.id}` : ""
                    }`
              }
            />
            <DetailField
              label={t("admin.capabilities.detail.catalogState")}
              value={
                detailItem.catalogState === "observed"
                  ? t("admin.capabilities.catalogState.observed")
                  : t("admin.capabilities.catalogState.registered")
              }
            />
            <DetailField
              label={t("admin.capabilities.detail.availability")}
              value={`${t("admin.capabilities.availability.discover")}: ${
                detailItem.availability.discover
                  ? t("admin.capabilities.yes")
                  : t("admin.capabilities.no")
              } · ${t("admin.capabilities.availability.use")}: ${
                detailItem.availability.use
                  ? t("admin.capabilities.yes")
                  : t("admin.capabilities.no")
              }`}
            />
            <DetailField
              label={t("admin.capabilities.detail.sourceCoverage")}
              value={`${detailItem.sourceCoverage.sources.join(", ")} · ${healthLabel(
                detailItem.sourceCoverage.readiness.status,
              )}`}
            />
            {detailItem.catalogState === "registered" ? (
              <>
                <DetailField
                  label={t("admin.capabilities.detail.definitionId")}
                  value={detailItem.definitionId}
                />
                <DetailField
                  label={t("admin.capabilities.detail.releaseId")}
                  value={detailItem.releaseId}
                />
                <DetailField
                  label={t("admin.capabilities.detail.version")}
                  value={detailItem.version}
                />
                <DetailField
                  label={t("admin.capabilities.detail.status")}
                  value={t(`admin.capabilities.releaseStatus.${detailItem.status}`)}
                />
                <DetailField
                  label={t("admin.capabilities.detail.deliveryMode")}
                  value={
                    detailItem.deliveryMode === "local_editable"
                      ? t("admin.capabilities.delivery.local_editable")
                      : t("admin.capabilities.delivery.remote_protected")
                  }
                />
                <DetailField
                  label={t("admin.capabilities.detail.deployment")}
                  value={t(`admin.capabilities.deployment.${detailItem.deployment}`)}
                />
                <DetailField
                  label={t("admin.capabilities.detail.executionKinds")}
                  value={detailItem.executionKinds
                    .map((k) => t(`admin.capabilities.executionKind.${k}`))
                    .join(", ")}
                />
              </>
            ) : (
              <>
                <DetailField
                  label={t("admin.capabilities.detail.deliveryMode")}
                  value={t("admin.capabilities.observed.deliveryUndeclared")}
                />
                <DetailField
                  label={t("admin.capabilities.detail.version")}
                  value={t("admin.capabilities.observed.noRelease")}
                />
              </>
            )}
          </div>
        ) : null}
      </Drawer>
    </div>
  );
}
